import React, { useContext, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../../../context/AuthContext'
import { DashboardContext } from '../../../context/DashboardContext'
import { ScheduleInterview } from './ScheduleInterview'
import { JoinLiveMeeting } from './JoinLiveMeeting'
import { UploadRecordings } from './UploadRecordings'
import Meetings from '../../../assets/welcomedashboard/apps/meetings.png'
import Calendars from '../../../assets/welcomedashboard/apps/calendars.png'
import Upload from '../../../assets/welcomedashboard/apps/upload.png'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import useProjectSelection from '../../../hooks/useProjectSelection'
import { useScreenSize } from '@/lib/useScreenSize'
import { Project } from '@/api-client'
import { cn } from '@/lib/utils'

/**
 * A React component that renders dynamic items with configurable layout and actions.
 * This component provides functionality for meeting links, calendar scheduling, and file uploading,
 * adapting its presentation for small screens and user requirements.
 *
 * @param {Object} props - The component props.
 * @param {boolean} [props.vertical=false] - Determines the orientation of the items. If true, items are rendered in a vertical layout.
 * @param {Object} [props.project=null] - The project object used to configure certain actions like generating calendar links or project-related actions.
 * @param {string} [props.className] - The CSS class name to be applied to the container.
 * @return {JSX.Element} Returns the rendered component with dynamic items based on the provided props.
 */
export function DynamicItems({
  vertical = false,
  project = null,
  className,
}: {
  vertical?: boolean
  project?: Project | null
  className?: string
}) {
  const { user } = useContext(AuthContext)
  const { shouldFireZoomToast, activeWorkspace } = useContext(DashboardContext)
  const [showSelectProjectDialog, setShowSelectProjectDialog] = useState(false)
  const [importFile, setImportFile] = useState(false)
  const [link, setLink] = useState('')
  const [showImportFileDialog, setShowImportFileDialog] = useState(false)
  const navigate = useNavigate()
  const customFetch = useCustomFetch()
  const { handleChooseExistingProject } = useProjectSelection(link, importFile)
  const { isSmallScreen } = useScreenSize()

  async function copyToClipboard() {
    try {
      project
        ? await navigator.clipboard.writeText(`invite+${project.stem}@evermuse.com`)
        : await navigator.clipboard.writeText(`invite+${activeWorkspace.stem}@evermuse.com`)
      toast.success('Copied!')
    } catch {
      toast.error('Failed to copy!')
    }
  }

  const handleDialog = () => {
    if (project) {
      setShowImportFileDialog(true)
    } else {
      setImportFile(true)
      setShowSelectProjectDialog(true)
    }
  }

  const generateCalendarLink = async () => {
    if (user.integrations?.googleCalendar) {
      const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE'
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(
        project !== null ? `invite+${project.stem}@evermuse.com` : `invite+${activeWorkspace.stem}@evermuse.com`
      )
      const eventLink = `${baseUrl}&text=${title}&add=${guests}`
      window.open(eventLink, '_blank')
    } else if (user.integrations?.microsoftCalendar) {
      const { isBusinessAccount, error } = await customFetch('/auth/isBusinessAccount')
      if (error) return toast.error('Failed to generate calendar link')
      const baseUrl = `https://outlook.${isBusinessAccount ? 'office' : 'live'}.com/calendar/0/deeplink/compose`
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(
        project !== null ? `invite+${project.stem}@evermuse.com` : `invite+${activeWorkspace.stem}@evermuse.com`
      )
      const eventLink = `${baseUrl}?subject=${title}&to=${guests}`
      window.open(eventLink, '_blank')
    } else {
      navigate('/dashboard/integrations')
    }
  }

  function handleLink() {
    if (link) {
      try {
        const _parsedUrl = new URL(link)
        const fireToast = shouldFireZoomToast(link)
        if (!fireToast && !project) setShowSelectProjectDialog(true)
        if (!fireToast && project) handleChooseExistingProject(project)
      } catch {
        toast.error('Please enter a valid link')
      }
    } else {
      toast.error('Please enter a link')
    }
  }

  return (
    <div className={cn('px-10', className, vertical ? 'flex flex-col pt-10 gap-8' : 'grid grid-cols-3 gap-8')}>
      <DynamicItemPanel
        vertical={vertical || isSmallScreen}
        imageSrc={Meetings}
        altText="meetings"
        headerClassName="bg-[#E2E8B7]"
        imageSize={{ vertical: 'w-[150px]', horizontal: 'w-[140px]' }}
      >
        <JoinLiveMeeting
          className="bg-[#f8ffc8]"
          onChange={(e) => setLink(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleLink()
          }}
          showDialog={showSelectProjectDialog}
          link={link}
          importFile={importFile}
          handleClose={() => {
            setImportFile(false)
            setShowSelectProjectDialog(false)
          }}
          onClick={() => handleLink()}
        />
      </DynamicItemPanel>

      <DynamicItemPanel
        vertical={vertical || isSmallScreen}
        imageSrc={Calendars}
        altText="calendars"
        headerClassName="bg-[#FED7C8]"
        imageSize={{ vertical: 'w-[180px]', horizontal: 'w-[185px]' }}
      >
        <ScheduleInterview
          className="bg-[#fee4d9]"
          onClick={copyToClipboard}
          project={project}
          activeWorkspace={activeWorkspace as any}
          onClick1={generateCalendarLink}
          user={user}
          isVertical={vertical || isSmallScreen}
        />
      </DynamicItemPanel>

      <DynamicItemPanel
        vertical={vertical || isSmallScreen}
        imageSrc={Upload}
        altText="upload"
        headerClassName="bg-[#ABE6FC]"
        imageSize={{ vertical: 'w-[65px]', horizontal: 'w-[60px]' }}
      >
        <UploadRecordings
          className="bg-[#cdf2ff]"
          onClick={() => handleDialog()}
          project={project}
          showImportDialog={showImportFileDialog}
          handleClose={() => {
            setShowImportFileDialog(false)
          }}
        />
      </DynamicItemPanel>
    </div>
  )
}

/**
 * Renders a dynamic item panel with an image, customizable background, and children elements.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.vertical - Determines the layout orientation of the panel. If true, the panel uses a vertical layout.
 * @param {string} props.imageSrc - The source URL for the image to be displayed in the panel.
 * @param {string} props.altText - The alt text for the image, used for accessibility.
 * @param {string} props.backgroundColor - The background color for the panel.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the panel.
 * @param {Object} props.imageSize - The image size configuration for vertical and horizontal layouts.
 * @param {string} props.imageSize.vertical - The CSS classes to apply for the image size in a vertical layout.
 * @param {string} props.imageSize.horizontal - The CSS classes to apply for the image size in a horizontal layout.
 *
 * @return {JSX.Element} The rendered DynamicItemPanel component.
 */
export function DynamicItemPanel({
  className,
  vertical,
  image,
  imageSrc,
  altText,
  imageSize,
  headerClassName,
  children,
}: {
  className?: string
  vertical: boolean
  image?: React.ReactNode
  imageSrc?: string
  altText: string
  imageSize: { vertical: string; horizontal: string }
  headerClassName?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn('rounded-xl h-full shadow-lg flex flex-col overflow-hidden', className)}
      style={{
        minWidth: '340px',
        width: '100%',
      }}
    >
      {/* Header */}
      <div
        className={cn(
          'rounded-t-xl flex justify-center items-center overflow-hidden h-[100px] flex-shrink-0',
          headerClassName
        )}
      >
        {image || (
          <img src={imageSrc} alt={altText} className={cn(vertical ? imageSize.vertical : imageSize.horizontal)} />
        )}
      </div>
      {/* Content */}
      {children}
    </div>
  )
}
